import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import { LT_ROUTE } from 'bl/loggers.bl';
import { fromSalonListObjectToSalonsInfo } from 'bl/salon/fromSalonListObjectToSalonsInfo.bl';
import PinLockedRoutes from 'components/core/PinLockedRoutes.component';
import ReloadChecker from 'components/core/ReloadChecker.component';
import UserMessages from 'components/core/UserMessages.component';
import VersionBanner from 'components/core/VersionBanner.component';
import Experiments from 'components/experiment/Experiments.component';
import Poc from 'components/experiment/poc/Poc.component';
import ForgotPasswordPage from 'components/login/ForgotPasswordPage.component';
import { NoAccessLandingPanel } from 'components/messages/NoAccessPanel.component';
import CodeVerification from 'components/onboarding/CodeVerification.component';
import { QuickSetupTypeWrap } from 'components/onboarding/quickSetupType/QuickSetupTypeWrap.component';
import { RegisterPage } from 'components/onboarding/RegisterPage.component';
import { SalonSetupInfo } from 'components/onboarding/SalonSetupInfo.component';
import { SalonSetupPage } from 'components/onboarding/SalonSetupPage.component';
import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import LINK from 'constants/link.const';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectSalonList } from 'selectors/salonInfo.selectors';
import { COGNITO_STATE, selectCognitoState } from 'utils/cognito.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { timeSub03days, timeSub30min } from 'utils/date/manipulate.util';
import { nowAsZulu } from 'utils/date/now.util';
import determineFbSalonId from 'utils/determineFbSalonId.util';
import { isEnvDev, isEnvNotProd, isEnvQA, isEnvStage } from 'utils/env/determine.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import { isNotValidId, isValidId } from 'utils/predicates/fields.util';

import NotFound from 'lib/NotFoundComponent';
import PrivateRoute from 'lib/PrivateRoute';
import SalonRoute from 'lib/SalonRoute';
import NotificationContainer from 'notification/index';
import CategoriesScreenWrap from 'onboarding/categories/CategoriesScreenWrap';
import OpeningHoursWrap from 'onboarding/openingHours/OpeningHoursWrap';
import AddRoleWrap from 'onboarding/roles/AddRoleWrap';
import RolesScreenWrap from 'onboarding/roles/RolesScreenWrap';
import SalonLoaderContainer from 'onboarding/SalonLoader';
import AddServiceWrap from 'onboarding/services/AddServiceWrap';
import EditCategoryWrap from 'onboarding/services/EditCategoryWrap';
import ServicesScreenWrap from 'onboarding/services/ServicesScreenWrap';
import AddStylistWrap from 'onboarding/stylists/AddStylistWrap';
import StylistsScreenWrap from 'onboarding/stylists/StylistsScreenWrap';
import Salon from 'salon/Salon.component';
import Login from 'user/components/Login';
import ResetPassword from 'user/components/ResetPassword';

import CN from './Application.module.scss';

const CTAG = 'Application';
const L = logger([CTAG, LT_ROUTE]);

const IS_STAGE = isEnvStage();
const IS_QA = isEnvQA();
const IS_DEV = isEnvDev();

const ReactQueryDevtools = React.lazy(() => import('@tanstack/react-query-devtools').then(($) => ({ default: $.ReactQueryDevtools })));

const href = window?.location?.href;

const useTitleUpdate = () => {
  useEffect(() => {
    if (!IS_STAGE && !IS_DEV && !IS_QA) return;

    const listener = () => {
      document.title = `SLICK (${fromAnyToString(window.location.hash)}) (DEVELOPMENT)`;
    };

    listener();

    try {
      window.navigation.addEventListener('navigatesuccess', listener);
    } catch (e) {
      L.debug('useTitleUpdate()', 'error:', fromAnyToString(e));
    }

    return () => {
      try {
        window.navigation.removeEventListener(listener);
      } catch (e) {
        L.debug('useTitleUpdate()', 'error:', fromAnyToString(e));
      }
    };
  }, []);
};

const SalonWithIdFromFacebook = () => {
  const now = nowAsZulu();
  const salonIdFromFb = determineFbSalonId();

  L.info('Opening salon with ID from Facebook:', salonIdFromFb, 'at:', now);
  return <Salon salonId={salonIdFromFb} />;
};

const LoginWithSalonIdFromHref = () => {
  const now = nowAsZulu();
  const salonIdFromHref = href.indexOf('state=') > 0 ? href.split('state=')[1] : void 1;

  L.info('Opening login with ID from href:', salonIdFromHref, 'at:', now);
  return <Login salonId={salonIdFromHref} />;
};

const SignupRoutesInternal = ({ cognitoState, salonList }) => {
  const salonId = useCurrentSalonId();
  const isLoggedIn = COGNITO_STATE.in === cognitoState;

  const { isOk, hasRole } = useMemo(() => fromSalonListObjectToSalonsInfo(salonList), [salonList]);

  if (isLoggedIn && isNotValidId(salonId) && isOk && hasRole) {
    const pathname = linkTo({ pattern: LINK.salonPick });
    L.info('User is already logged in, redirecting to salon picker:', pathname, '...');

    return <Redirect to={{ pathname }} />;
  }

  if (isLoggedIn && isValidId(salonId) && isOk && hasRole) {
    const pathname = linkTo({ pattern: LINK.salonToday, params: { salonId } });
    L.info("User is already logged in, redirecting to today's scheduler:", pathname, '...');

    return <Redirect to={{ pathname }} />;
  }

  return (
    <div data-bem="SignupRoutes" className={CN.signUp}>
      <Switch>
        {/* NOTE: these routes should not be under PrivateRoute since that one will keep redirecting to itself */}
        <Route exact path={LINK.onboarding} component={RegisterPage} />
        <Route path={LINK.onboardingSms} component={CodeVerification} />

        {/* PrivateRoute has logic that redirects based in Cognito state */}
        <PrivateRoute path={LINK.onboardingInfo} component={SalonSetupInfo} />
        <PrivateRoute path={LINK.onboardingType} component={QuickSetupTypeWrap} />
        <PrivateRoute path={LINK.onboardingSalon} component={SalonSetupPage} />

        {/* // NOTE: old routes, not in use with the new sign up UI */}
        <PrivateRoute path={LINK.onboardingRoles} component={RolesScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddRole} component={AddRoleWrap} />
        <PrivateRoute path={LINK.onboardingCategories} component={CategoriesScreenWrap} />
        <PrivateRoute path={LINK.onboardingServices} component={ServicesScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddService} component={AddServiceWrap} />
        <PrivateRoute path={LINK.onboardingStylists} component={StylistsScreenWrap} />
        <PrivateRoute path={LINK.onboardingAddStylist} component={AddStylistWrap} />
        <PrivateRoute path={LINK.onboardingEditCategory} component={EditCategoryWrap} />
        <PrivateRoute path={LINK.onboardingOpeningHours} component={OpeningHoursWrap} />
      </Switch>
    </div>
  );
};

const SignupRoutes = connect((state) => ({
  cognitoState: selectCognitoState(state),
  salonList: selectSalonList(state),
}))(SignupRoutesInternal);

const TopRoutes = () => (
  <Switch>
    <Route path="/poc/" component={Poc} />

    <Route exact path={LINK.root} component={LoginWithSalonIdFromHref} />
    <Route exact path="/login" component={LoginWithSalonIdFromHref} />
    <Route path="/fbe-auth" component={LoginWithSalonIdFromHref} />
    <Route path="/login/forgot" component={ForgotPasswordPage} />
    <Route path="/login/forcereset" component={ResetPassword} />
    <Route path="/no-access" component={NoAccessLandingPanel} />
    <Route path="/signup" component={SignupRoutes} />

    <PrivateRoute exact path="/salon" component={SalonWithIdFromFacebook} />
    <SalonRoute path="/setup/salon/:id" component={SalonLoaderContainer} />
    <Route path="/salon/:id" component={PinLockedRoutes} />

    {fromAnyToBool(CONF.experiments) && <Route path="/x" component={Experiments} />}

    <Route component={NotFound} />
  </Switch>
);

const Application = () => {
  useTitleUpdate();

  // TODO: move this cutoff deeper, maybe even into the RELEASE singleton and use environment variables for it
  const cutoff = isEnvStage() || isEnvQA() ? timeSub30min : timeSub03days;

  const isQueryToolsRendered = isEnvNotProd() && fromAnyToBool(FLAG.reactQueryDevtools);
  const isMqRendered = fromAnyToBool(FLAG.mediaQ);

  return (
    <div data-bem="Application" className="root-container">
      {isMqRendered && (
        <p data-bem="Mq" className={CN.component}>
          mq
        </p>
      )}

      {isQueryToolsRendered && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtools />
        </React.Suspense>
      )}

      <NotificationContainer />
      <VersionBanner />
      <UserMessages />
      <ReloadChecker tag={CTAG} cutoff={cutoff} />

      <TopRoutes />
    </div>
  );
};

export default Application;
